import type { ServerConnection, ServerSummary } from "@woven/contracts";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useAccountCapacity } from "../capacity/AccountCapacityContext";
import { ControlApiError } from "../../lib/api";
import { useControlApiClient } from "../../lib/hooks";
import { useAuth } from "../../auth/context";
import { ReauthenticateModal } from "../../components/ReauthenticateModal";

export function ServerActions({
  server,
  reload,
  children,
}: {
  server: ServerSummary;
  reload: () => void;
  children: ReactNode;
}) {
  const client = useControlApiClient();
  const { user } = useAuth();
  const { refresh } = useAccountCapacity();
  const navigate = useNavigate();
  const scope = useMemo(
    () => ({
      client,
      ownerUid: user?.uid,
      id: server.id,
      generation: server.generation,
      endpoint: server.connection?.endpoint,
      namespaceId: server.connection?.namespaceId,
      sessionId: server.connection?.sessionId,
      deleteIntent: server.lifecycle?.deleteIntent,
      recoveryPending: server.lifecycle?.recoveryPending,
    }),
    [
      client,
      user?.uid,
      server.id,
      server.generation,
      server.connection?.endpoint,
      server.connection?.namespaceId,
      server.connection?.sessionId,
      server.lifecycle?.deleteIntent,
      server.lifecycle?.recoveryPending,
    ]
  );
  const sessionRef = useRef<{ scope: typeof scope; controller: AbortController | null } | null>(
    null
  );
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    connection?: ServerConnection;
    busy?: string;
    error?: string;
    message?: string;
    reauthenticationRequired?: boolean;
    retryLabel?: string;
    copied?: boolean;
    tlsOpen?: boolean;
    tlsCopied?: boolean;
    tlsCopyError?: string;
  } | null>(null);
  const [connectionDialog, setConnectionDialog] = useState<typeof scope | null>(null);
  const [deletion, setDeletion] = useState<{ scope: typeof scope; confirmation: string } | null>(
    null
  );
  const [recovery, setRecovery] = useState<{ scope: typeof scope; confirmation: string } | null>(
    null
  );
  const recovering = recovery?.scope === scope;
  const recoveryConfirmation = recovering ? recovery.confirmation : "";
  const [reauthentication, setReauthentication] = useState<typeof scope | null>(null);
  const connectionOpen = connectionDialog === scope;
  const confirming = deletion?.scope === scope;
  const confirmation = confirming ? deletion.confirmation : "";
  const current = snapshot?.scope === scope ? snapshot : null;
  const deleting = current?.busy === "delete" || current?.busy === "delete-recovery";
  useLayoutEffect(() => {
    const session = { scope, controller: null as AbortController | null };
    sessionRef.current = session;
    return () => {
      sessionRef.current = null;
      session.controller?.abort();
      setSnapshot((value) => (value?.scope === scope ? null : value));
      setConnectionDialog((value) => (value === scope ? null : value));
      setDeletion((value) => (value?.scope === scope ? null : value));
      setRecovery((value) => (value?.scope === scope ? null : value));
      setReauthentication((value) => (value === scope ? null : value));
    };
  }, [scope]);
  const provisioned = server.connection?.tokenStatus === "active" && !!server.connection.endpoint;
  const canReveal =
    provisioned &&
    !!scope.generation &&
    !!scope.namespaceId &&
    !!scope.sessionId &&
    !scope.deleteIntent &&
    !scope.recoveryPending;
  const canProvision =
    !!scope.generation &&
    server.lifecycle?.canProvision === true &&
    !scope.recoveryPending &&
    !scope.deleteIntent &&
    (server.status === "pending" || server.status === "provisioning");

  const canRecover =
    !!scope.generation &&
    (scope.recoveryPending === true ||
      (!!scope.endpoint && !!scope.namespaceId && !!scope.sessionId)) &&
    !scope.deleteIntent &&
    (server.status === "running" || server.status === "provisioning");

  const canRecoverDeletion = !!scope.generation && scope.deleteIntent === true;

  async function run(
    action: "connection" | "provision" | "delete" | "recover" | "delete-recovery"
  ) {
    const session = sessionRef.current;
    if (
      !session ||
      session.scope !== scope ||
      session.controller ||
      !scope.ownerUid ||
      !scope.generation
    )
      return;
    if (action === "connection" && !canReveal) return;
    if (action === "provision" && !canProvision) return;
    if (
      (action === "delete" || action === "delete-recovery") &&
      (!confirming || confirmation !== "DELETE")
    )
      return;
    if (action === "delete-recovery" && !canRecoverDeletion) return;
    if (action === "recover" && (!canRecover || !recovering || recoveryConfirmation !== "RECOVER"))
      return;
    const controller = new AbortController();
    session.controller = controller;
    setReauthentication(null);
    setSnapshot({ scope, busy: action });
    const active = () => sessionRef.current === session && !controller.signal.aborted;
    try {
      if (action === "connection") {
        const connection = await client.getServerConnection(
          scope.id,
          { generation: scope.generation },
          controller.signal
        );
        if (active()) {
          if (
            connection.endpoint !== scope.endpoint ||
            connection.namespaceId !== scope.namespaceId ||
            connection.sessionId !== scope.sessionId
          ) {
            setSnapshot({
              scope,
              error:
                "Connection scope changed. Refresh server status before revealing details again.",
            });
          } else setSnapshot({ scope, connection });
        }
      } else if (action === "provision" || action === "recover") {
        const response =
          action === "recover"
            ? await client.recoverServer(
                scope.id,
                { generation: scope.generation, confirmation: "RECOVER" },
                controller.signal
              )
            : await client.provisionServer(
                scope.id,
                { generation: scope.generation },
                controller.signal
              );
        if (active()) {
          if (response.server.id !== scope.id || response.server.generation !== scope.generation) {
            setSnapshot({
              scope,
              error: "Server generation changed. Refresh server status before retrying.",
            });
          } else {
            setSnapshot({ scope });
            if (action === "recover") setRecovery(null);
            reload();
          }
        }
      } else {
        if (action === "delete-recovery") {
          await client.recoverServerDeletion(
            scope.id,
            { confirmation: "DELETE", generation: scope.generation },
            controller.signal
          );
        } else {
          await client.deleteServer(
            scope.id,
            { confirmation: "DELETE", generation: scope.generation },
            controller.signal
          );
        }
        if (active()) {
          refresh();
          navigate("/console/servers");
        }
      }
    } catch (error) {
      if (active()) {
        const code = error instanceof ControlApiError ? error.code : null;
        const retryLabel =
          action === "delete-recovery"
            ? "Finish Deletion After Restart"
            : action === "delete"
              ? "Confirm Delete Server"
              : action === "recover"
                ? "Confirm Recover Session"
                : "Reveal Connection Details";
        setSnapshot({
          retryLabel,
          scope,
          reauthenticationRequired: code === "RECENT_AUTH_REQUIRED",
          error:
            code === "RECENT_AUTH_REQUIRED"
              ? `Reauthenticate with the current account, then press ${retryLabel} again.`
              : code === "UNAUTHENTICATED"
                ? "Your session is no longer valid. Sign Out, then sign in again. When using the local emulator, create a new local account before retrying."
                : code === "PERMISSION_DENIED"
                  ? "The current account does not have permission to perform this server action."
                  : code === "UNAVAILABLE"
                    ? action === "recover"
                      ? "Recovery not confirmed. A recovery may have been saved or applied. Refresh server status, then explicitly retry recovery; do not retry provisioning."
                      : action === "delete-recovery"
                        ? "Deletion not confirmed. The product and capacity remain reserved until teardown succeeds. Refresh server status, then explicitly retry Finish Deletion After Restart."
                        : "Managed provisioning or the node is unavailable. Ask the operator to enable or restore it, then retry."
                    : "Operation not confirmed. Refresh server status before retrying. If deletion failed, the server and CCU allocation remain reserved until teardown succeeds.",
        });
      }
    } finally {
      if (session.controller === controller) session.controller = null;
    }
  }

  function closeConnectionDialog() {
    if (current?.busy || current?.tlsOpen || reauthentication === scope) return;
    setConnectionDialog(null);
    setReauthentication(null);
    setSnapshot(null);
  }

  function closeDeletionDialog() {
    if (deleting || reauthentication === scope) return;
    setReauthentication(null);
    setDeletion(null);
    setSnapshot(null);
  }

  function closeRecoveryDialog() {
    if (current?.busy || reauthentication === scope) return;
    setRecovery(null);
    setSnapshot(null);
  }

  useEffect(() => {
    if (!connectionOpen && !confirming && !recovering) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (connectionOpen) closeConnectionDialog();
      else if (recovering) closeRecoveryDialog();
      else closeDeletionDialog();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  });

  async function copyToken() {
    const session = sessionRef.current;
    const connection = current?.connection;
    if (!connection || !session || session.scope !== scope) return;
    try {
      await navigator.clipboard.writeText(connection.token);
      if (sessionRef.current === session)
        setSnapshot((value) =>
          value?.connection === connection ? { ...value, copied: true } : value
        );
    } catch {
      if (sessionRef.current === session)
        setSnapshot((value) =>
          value?.connection === connection
            ? {
                ...value,
                error: "Clipboard unavailable. Select the revealed token to copy it manually.",
              }
            : value
        );
    }
  }

  function setTlsInfoOpen(open: boolean) {
    const connection = current?.connection;
    if (!connection?.caPem) return;
    setSnapshot((value) =>
      value && value.connection === connection
        ? {
            ...value,
            tlsOpen: open,
            tlsCopied: false,
            tlsCopyError: undefined,
          }
        : value
    );
  }

  async function copyTlsCertificate() {
    const session = sessionRef.current;
    const connection = current?.connection;
    if (!connection?.caPem || !session || session.scope !== scope) return;
    try {
      await navigator.clipboard.writeText(connection.caPem);
      if (sessionRef.current === session)
        setSnapshot((value) =>
          value?.connection === connection
            ? { ...value, tlsCopied: true, tlsCopyError: undefined }
            : value
        );
    } catch {
      if (sessionRef.current === session)
        setSnapshot((value) =>
          value?.connection === connection
            ? {
                ...value,
                tlsCopied: false,
                tlsCopyError:
                  "Clipboard unavailable. Select the certificate text to copy it manually.",
              }
            : value
        );
    }
  }

  return (
    <>
      <section className="panel server-detail-summary" aria-label="Server details">
        <div className="server-actions server-detail-summary-actions">
          <Link
            className="btn btn--ghost server-summary-action"
            to={`/console/logs?serverId=${encodeURIComponent(server.id)}`}
          >
            View Logs
          </Link>
          <button
            className="btn btn--primary server-summary-action"
            disabled={!!current?.busy}
            onClick={() => {
              setDeletion(null);
              setRecovery(null);
              setReauthentication(null);
              setSnapshot({ scope });
              setConnectionDialog(scope);
            }}
          >
            Connect
          </button>
          {canRecover && (
            <button
              className="btn btn--ghost server-summary-action"
              disabled={!!current?.busy}
              onClick={() => {
                setConnectionDialog(null);
                setDeletion(null);
                setReauthentication(null);
                setSnapshot({ scope });
                setRecovery({ scope, confirmation: "" });
              }}
            >
              Recover Session
            </button>
          )}
          <button
            className="btn btn--danger server-summary-action"
            disabled={!scope.generation || !!current?.busy}
            onClick={() => {
              setConnectionDialog(null);
              setReauthentication(null);
              setRecovery(null);
              setDeletion({ scope, confirmation: "" });
              setSnapshot({ scope });
            }}
          >
            Delete Server
          </button>
        </div>
        {children}
      </section>
      {connectionOpen && (
        <div className="modal-backdrop" onClick={closeConnectionDialog}>
          <section
            className="modal server-connection-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="server-connection-title"
            aria-describedby="server-connection-description"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title" id="server-connection-title">
                Connection Details
              </h2>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!!current?.busy}
                onClick={closeConnectionDialog}
                autoFocus
              >
                Close
              </button>
            </div>
            <p id="server-connection-description">
              Token required · {provisioned ? "Active" : "Unavailable"}
            </p>
            {!provisioned && (
              <p>
                Not provisioned. A pending registration is desired configuration only; no usable
                endpoint or token is available.
              </p>
            )}
            {!scope.generation && (
              <p>
                Server version unavailable. Refresh server status; if it remains unavailable,
                contact support. Lifecycle actions are disabled.
              </p>
            )}
            {scope.deleteIntent && (
              <p>
                Deletion is in progress. Provisioning and connection retrieval are disabled; retry
                deletion to complete teardown.
              </p>
            )}
            {scope.recoveryPending && (
              <p>
                Session recovery is pending. Close this dialog and explicitly use Recover Session to
                retry. Provisioning and connection retrieval are disabled.
              </p>
            )}
            {canProvision && (
              <>
                <p>
                  Retry uses the saved configuration. Managed provisioning must be enabled by the
                  operator.
                </p>
                <button
                  className="btn btn--ghost"
                  disabled={!!current?.busy}
                  onClick={() => void run("provision")}
                >
                  {current?.busy === "provision" ? "Provisioning…" : "Retry Provision"}
                </button>
              </>
            )}

            {!current?.connection ? (
              <>
                <p aria-label="Masked token" className="cell-mono">
                  ••••••••••••••••
                </p>
                <button
                  className="btn btn--primary"
                  disabled={!canReveal || !!current?.busy}
                  onClick={() => void run("connection")}
                >
                  {current?.busy === "connection"
                    ? "Retrieving Connection…"
                    : "Reveal Connection Details"}
                </button>
              </>
            ) : (
              <>
                <dl className="detail-list">
                  {Object.entries({
                    "Server ID": scope.id,
                    Generation: scope.generation,
                    "Native QUIC Endpoint": current.connection.endpoint,
                    "Native Transport": current.connection.transport,
                    ...(current.connection.webTransport
                      ? {
                          "Browser WebTransport URL": current.connection.webTransport.url,
                        }
                      : {}),
                    ...(current.connection.webTransport?.certificateSha256
                      ? {
                          "Browser Certificate SHA-256":
                            current.connection.webTransport.certificateSha256,
                        }
                      : {}),
                    "Namespace ID": current.connection.namespaceId,
                    "Session ID": current.connection.sessionId,
                  }).map(([label, value]) => (
                    <div className="detail-row" key={label}>
                      <dt>{label}</dt>
                      <dd className="cell-mono">{value}</dd>
                    </div>
                  ))}
                </dl>
                <h3>Logical Spaces</h3>
                <ul>
                  {current.connection.spaces
                    .filter((space) => space.kind === "logical")
                    .map((space) => (
                      <li key={space.spaceId}>
                        Space {space.spaceId} · Epoch {space.epoch} · Channels{" "}
                        {space.channelIds.join(", ")}
                      </li>
                    ))}
                </ul>
                <h3>Spatial Subspaces</h3>
                {current.connection.spaces.some((space) => space.kind === "spatialGrid3D") ? (
                  <ul>
                    {current.connection.spaces
                      .filter((space) => space.kind === "spatialGrid3D")
                      .map((space) => (
                        <li key={space.spaceId}>
                          {space.displayName} ({space.slug}) · Space {space.spaceId} · Epoch{" "}
                          {space.epoch} · Channels {space.channelIds.join(", ")} · Bounds (
                          {space.bounds.min.x}, {space.bounds.min.y}, {space.bounds.min.z}) to (
                          {space.bounds.max.x}, {space.bounds.max.y}, {space.bounds.max.z}) · Cell{" "}
                          {space.cellSize} · Radius {space.interestRadius} · {space.metersPerUnit}{" "}
                          m/unit · Exact distance {space.exactDistance ? "on" : "off"}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p>No user-configured spatial subspaces.</p>
                )}
                <label>
                  Connection Token
                  <textarea
                    readOnly
                    autoComplete="off"
                    spellCheck={false}
                    value={current.connection.token}
                  />
                </label>
                <p>
                  This is a shared product-generation possession credential for trusted/internal
                  clients, not end-user identity. If it may be compromised, delete and recreate the
                  product; token rotation and short-lived grants are not available yet.
                </p>
                <div className="server-actions">
                  <button className="btn btn--ghost" onClick={() => void copyToken()}>
                    Copy Token
                  </button>
                  {current.connection.caPem && (
                    <button className="btn btn--ghost" onClick={() => setTlsInfoOpen(true)}>
                      View TLS Info
                    </button>
                  )}
                  <button className="btn btn--ghost" onClick={() => setSnapshot({ scope })}>
                    Hide Connection Details
                  </button>
                </div>
                {current.copied && <p role="status">Token copied to clipboard.</p>}
                {current.connection.caPem && current.tlsOpen && (
                  <TlsInfoModal
                    certificate={current.connection.caPem}
                    copied={!!current.tlsCopied}
                    copyError={current.tlsCopyError}
                    onCopy={() => void copyTlsCertificate()}
                    onClose={() => setTlsInfoOpen(false)}
                  />
                )}
              </>
            )}
            {current?.error && <p role="alert">{current.error}</p>}
            {current?.message && <p role="status">{current.message}</p>}
            {current?.reauthenticationRequired && (
              <button className="btn btn--primary" onClick={() => setReauthentication(scope)}>
                Reauthenticate
              </button>
            )}
            {current?.error && (
              <button className="btn btn--ghost" onClick={reload}>
                Refresh Server Status
              </button>
            )}
          </section>
        </div>
      )}
      {confirming && (
        <div className="modal-backdrop" onClick={closeDeletionDialog}>
          <section
            className="modal server-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="server-delete-title"
            aria-describedby="server-delete-description"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title" id="server-delete-title">
                Delete {server.name} ({server.id})?
              </h2>
            </div>
            <p id="server-delete-description">
              Deletes this server and its logs. CCU is released only after managed teardown
              succeeds. A failure keeps the allocation reserved.
            </p>
            {canRecoverDeletion && (
              <p>
                Finish Deletion After Restart explicitly retries the original deletion on the
                restarted node. It permanently removes only this product’s reservation and
                credentials after confirmed teardown. It does not restore state or affect other
                products. This is separate from ordinary deletion and is never an automatic
                fallback.
              </p>
            )}
            <label>
              Type DELETE to Confirm
              <input
                value={confirmation}
                disabled={deleting || reauthentication === scope}
                onChange={(event) => setDeletion({ scope, confirmation: event.target.value })}
                autoComplete="off"
                autoFocus
              />
            </label>
            <div className="server-actions">
              <button
                className="btn btn--danger"
                disabled={confirmation !== "DELETE" || deleting || reauthentication === scope}
                onClick={() => void run("delete")}
              >
                {current?.busy === "delete" ? "Deleting…" : "Confirm Delete Server"}
              </button>
              {canRecoverDeletion && (
                <button
                  className="btn btn--danger"
                  disabled={confirmation !== "DELETE" || deleting || reauthentication === scope}
                  onClick={() => void run("delete-recovery")}
                >
                  {current?.busy === "delete-recovery"
                    ? "Finishing Deletion…"
                    : "Finish Deletion After Restart"}
                </button>
              )}
              <button
                className="btn btn--ghost"
                disabled={deleting || reauthentication === scope}
                onClick={closeDeletionDialog}
              >
                Cancel
              </button>
            </div>
            {current?.error && <p role="alert">{current.error}</p>}
            {current?.message && <p role="status">{current.message}</p>}
            {current?.reauthenticationRequired && (
              <button className="btn btn--primary" onClick={() => setReauthentication(scope)}>
                Reauthenticate
              </button>
            )}
            {current?.error && (
              <button className="btn btn--ghost" onClick={reload}>
                Refresh Server Status
              </button>
            )}
          </section>
        </div>
      )}
      {recovering && (
        <div className="modal-backdrop" onClick={closeRecoveryDialog}>
          <section
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="server-recover-title"
            aria-describedby="server-recover-description"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title" id="server-recover-title">
                Recover Session
              </h2>
            </div>
            <p id="server-recover-description">
              Recover {server.name} ({server.id}) after a node restart by creating a new empty
              session (runtime scope). Clients must refresh their connection descriptors and
              reconnect. This does not restore world or application state, live connections,
              entities, or messages. The registration and allocated capacity remain unchanged.
            </p>
            {scope.recoveryPending && (
              <p>A recovery is pending. This explicitly retries the saved recovery.</p>
            )}
            <label>
              Type RECOVER to Confirm
              <input
                value={recoveryConfirmation}
                disabled={!!current?.busy || reauthentication === scope}
                onChange={(event) => setRecovery({ scope, confirmation: event.target.value })}
                autoComplete="off"
                autoFocus
              />
            </label>
            <div className="server-actions">
              <button
                className="btn btn--danger"
                disabled={
                  !canRecover ||
                  recoveryConfirmation !== "RECOVER" ||
                  !!current?.busy ||
                  reauthentication === scope
                }
                onClick={() => void run("recover")}
              >
                {current?.busy === "recover" ? "Recovering…" : "Confirm Recover Session"}
              </button>
              <button
                className="btn btn--ghost"
                disabled={!!current?.busy || reauthentication === scope}
                onClick={closeRecoveryDialog}
              >
                Cancel
              </button>
            </div>
            {current?.error && <p role="alert">{current.error}</p>}
            {current?.message && <p role="status">{current.message}</p>}
            {current?.reauthenticationRequired && (
              <button className="btn btn--primary" onClick={() => setReauthentication(scope)}>
                Reauthenticate
              </button>
            )}
            {current?.error && (
              <button className="btn btn--ghost" onClick={reload}>
                Refresh Server Status
              </button>
            )}
          </section>
        </div>
      )}
      {reauthentication === scope && (connectionOpen || recovering || confirming) && (
        <ReauthenticateModal
          onClose={() => setReauthentication(null)}
          onSuccess={() => {
            if (sessionRef.current?.scope !== scope) return;
            setReauthentication(null);
            setSnapshot({
              scope,
              message: `Reauthentication complete. Press ${current?.retryLabel ?? "Reveal Connection Details"} again.`,
            });
          }}
        />
      )}
    </>
  );
}

function TlsInfoModal({
  certificate,
  copied,
  copyError,
  onCopy,
  onClose,
}: {
  certificate: string;
  copied: boolean;
  copyError?: string;
  onCopy: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section
        className="modal tls-info-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tls-info-title"
        aria-describedby="tls-info-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title" id="tls-info-title">
            TLS Info
          </h2>
          <div className="server-actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={onCopy}>
              Copy CA Certificate
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} autoFocus>
              Close
            </button>
          </div>
        </div>
        <p id="tls-info-description">
          Configure your client to trust this CA certificate when connecting to the QUIC endpoint.
          Validate the server certificate and endpoint hostname before authenticating with the
          server token.
        </p>
        <pre className="tls-info-certificate" tabIndex={0}>
          {certificate}
        </pre>
        {copied && <p role="status">CA certificate copied to clipboard.</p>}
        {copyError && <p role="alert">{copyError}</p>}
      </section>
    </div>
  );
}
